import { type Process } from '@/payload-types'
import { MegaMenuItem } from '@/ui/layout/Header/NavBar/MegaMenu/types'
import { AreaStructure } from '@/utils/getAreaStructure'
// localizedHref normalizes CMS paths to the new URL scheme (/workflows/...) and
// adds a locale prefix only for non-default locales (localePrefix 'as-needed'),
// so nav links resolve directly (HTTP 200) instead of 301-redirecting —
// saving crawl budget and preserving link equity.
import { localizedHref } from '@/utils/localizedHref'

export const mapAreaStructureToMegaMenuItem = (
  structure: AreaStructure,
  locale: string,
): MegaMenuItem => {
  return {
    label: 'Areas',
    categories: structure.docs
      .filter((area) => area.title && area.processes && area.processes.length > 0)
      .map((area) => {
        // Get processes and separate by comingSoon status
        const allProcesses = (area.processes || [])
          .map((processValue) => (typeof processValue === 'string' ? null : processValue))
          .filter((process): process is Process => process !== null)

        // Separate coming soon from available processes
        const availableProcesses = allProcesses.filter((p) => !p.isComingSoon)
        const comingSoonProcesses = allProcesses
          .filter((p) => p.isComingSoon)
          .sort((a, b) => (a.title || '').localeCompare(b.title || ''))

        // Combine: available first (in CMS order), then coming soon (alphabetically)
        const orderedProcesses = [...availableProcesses, ...comingSoonProcesses]

        return {
          title: area.title || '',
          links: orderedProcesses.map((process) => ({
            label: process.megaMenuTitle || process.title,
            isComingSoon: process.isComingSoon ?? false,
            href: localizedHref(process.path, locale),
            description: process.title,
            extraContents: process.coreStages
              ? {
                  items: (process.coreStages.stages || []).map((stageValue) => {
                    const stage = typeof stageValue === 'string' ? null : stageValue
                    return {
                      label: stage?.title || '',
                      href: localizedHref(stage?.path, locale),
                      iconName: stage?.coreStageIcon || undefined,
                    }
                  }),
                  image:
                    typeof process.previewCardFields?.thumbnail === 'string'
                      ? undefined
                      : process.previewCardFields?.thumbnail || undefined,
                }
              : undefined,
          })),
        }
      }),
  }
}
